// Data model for Batch 2 — 7 clinical hubs and 34 condition pages.
// Structured so content can later be moved to CMS without changing components.

export const HUBS = [
  {
    slug: "aortic",
    name: "Aortic & Complex Vascular",
    kicker: "Hub 01",
    lede: "The aorta doesn't tolerate delay, and it doesn't tolerate over-treatment.",
    intro:
      "Aortic aneurysms, dissections and complex vascular anomalies need a doctor who can read the imaging, decide the timing, and — when the answer is intervention — perform it themselves. Dr Sidhu does all three, in one continuous review.",
    image: "https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&w=1600&q=80",
    imageAlt: "Aortic imaging on a lightbox",
    keywords: ["aortic aneurysm mohali", "endovascular aortic repair india", "aortic dissection consultation"],
  },
  {
    slug: "pad",
    name: "Peripheral Artery Disease & Limb Salvage",
    kicker: "Hub 02",
    lede: "A limb saved is never modular. One doctor, from angiogram to the six-month review.",
    intro:
      "Peripheral artery disease, diabetic foot, critical limb ischaemia and carotid disease all share one requirement: the imaging read must be honest and the intervention timely. We image, decide, and — where appropriate — angioplasty in a single continuity of care.",
    image: "https://images.unsplash.com/photo-1579154204601-01588f351e67?auto=format&fit=crop&w=1600&q=80",
    imageAlt: "Angiography suite",
    keywords: ["peripheral artery disease treatment mohali", "diabetic foot angioplasty", "limb salvage india"],
  },
  {
    slug: "venous",
    name: "Venous Disease & DVT",
    kicker: "Hub 03",
    lede: "Deep vein disease is under-recognised, over-anticoagulated, and often solvable.",
    intro:
      "From leg-swelling and varicose veins to acute DVT and pulmonary embolism, venous cases require the same discipline as arterial ones — read the imaging carefully, choose the least invasive right answer, and follow through.",
    image: "https://images.unsplash.com/photo-1512069772995-ec65ed45afd6?auto=format&fit=crop&w=1600&q=80",
    imageAlt: "Ultrasound of veins",
    keywords: ["dvt treatment mohali", "varicose veins laser", "may-thurner syndrome"],
  },
  {
    slug: "oncology",
    name: "Interventional Oncology",
    kicker: "Hub 04",
    lede: "For many cancers, the least invasive treatment is now the best one.",
    intro:
      "Liver TACE and Y-90, kidney ablation, portal hypertension, biliary drainage, and long-term vascular access for chemotherapy — interventional oncology works in partnership with medical and surgical oncology teams. We do the image-guided part; we speak plainly about when it helps and when it doesn't.",
    image: "https://images.unsplash.com/photo-1631815589968-fdb09a223b1e?auto=format&fit=crop&w=1600&q=80",
    imageAlt: "CT-guided procedure",
    keywords: ["liver tace india", "y-90 radioembolisation", "kidney ablation mohali", "chemo port"],
  },
  {
    slug: "womens-health",
    name: "Women's Health — Fibroids & Pelvic",
    kicker: "Hub 05",
    lede: "Fibroids don't always need a hysterectomy. Pelvic pain doesn't always need silence.",
    intro:
      "Uterine artery embolisation and pelvic congestion treatment are day-case, uterus-sparing, and — for a large group of women — dramatically better than the alternatives they've been offered. We're honest about who's a good candidate and who isn't.",
    image: "https://images.unsplash.com/photo-1631815587646-b85a1bb027e1?auto=format&fit=crop&w=1600&q=80",
    imageAlt: "Women's health imaging",
    keywords: ["uterine fibroid embolisation india", "pelvic congestion treatment", "fibroid non-surgical"],
  },
  {
    slug: "mens-health",
    name: "Men's Health — Prostate & Varicocele",
    kicker: "Hub 06",
    lede: "Two conditions. Two under-known treatments. One conversation.",
    intro:
      "Prostate artery embolisation (PAE) for benign enlargement and varicocele embolisation for pain and fertility — both keyhole, both day-case, both preserving. If you've been told surgery is the only path, get a second read first.",
    image: "https://images.unsplash.com/photo-1666214280557-f1b5022eb634?auto=format&fit=crop&w=1600&q=80",
    imageAlt: "Interventional suite",
    keywords: ["prostate artery embolisation india", "varicocele embolisation mohali", "PAE non-surgical prostate"],
  },
  {
    slug: "dialysis-emergency",
    name: "Dialysis Access & Vascular Emergencies",
    kicker: "Hub 07",
    lede: "Fistula salvage. Bleeding, controlled. Access, protected. Time matters here.",
    intro:
      "For patients on dialysis, fistula health is life. For patients bleeding — GI, respiratory, or after trauma — embolisation buys time and, often, buys survival. This hub covers both: the ongoing access work and the sharp-end emergency IR.",
    image: "https://images.unsplash.com/photo-1516549655169-df83a0774514?auto=format&fit=crop&w=1600&q=80",
    imageAlt: "Emergency IR",
    keywords: ["dialysis fistula salvage india", "gi bleed embolisation", "trauma embolisation mohali"],
  },
];

// Every condition maps to exactly one hub via `hub` slug.
export const CONDITIONS = [
  // — Aortic —
  {
    slug: "aortic-aneurysm", name: "Aortic Aneurysm", hub: "aortic",
    oneLiner: "A weakened, enlarging section of the aorta — often silent until it isn't.",
    whatIsIt:
      "An aortic aneurysm is a bulge in the wall of the aorta — usually abdominal (AAA) or thoracic (TAA). Most are found incidentally on imaging done for another reason. Small aneurysms are watched; larger ones are repaired to prevent rupture.",
    symptoms: ["Often none until large or complicated", "Deep back or abdominal ache", "Pulsatile abdominal swelling", "Sudden severe pain (rupture — emergency)"],
    diagnosis: "Ultrasound, CT angiography, and MRI define size and shape. Serial imaging drives decisions.",
    treatment:
      "For anatomically-suitable aneurysms, endovascular aortic repair (EVAR / TEVAR) is a keyhole approach through the groin — no open chest or belly, shorter recovery. Where anatomy demands, we refer honestly to a vascular surgeon.",
    whenUrgent: "Sudden severe back or abdominal pain with a known aneurysm — go to emergency now.",
    faqs: [
      { q: "How big does an aneurysm need to be before repair?", a: "Thresholds vary by segment (typically 5.5 cm AAA in men, 5.0 cm in women), shape, growth rate, and patient factors. Size alone is not the whole answer." },
      { q: "Is EVAR permanent?", a: "EVAR is durable but requires lifelong imaging follow-up — usually annual CT. We coordinate this even for patients who return abroad." },
    ],
  },
  {
    slug: "aortic-dissection", name: "Aortic Dissection", hub: "aortic",
    oneLiner: "A tear inside the aortic wall that can be time-critical.",
    whatIsIt:
      "In a dissection, blood enters between the layers of the aortic wall. Type A (ascending) is a surgical emergency; Type B (descending) is often managed medically first, with endovascular repair (TEVAR) for complicated cases.",
    symptoms: ["Sudden tearing chest/back pain", "Difference between arms in blood pressure", "Neurological or limb symptoms if branches are compromised"],
    diagnosis: "Emergency CT angiography is the reference standard.",
    treatment:
      "Uncomplicated Type B: strict blood pressure control and surveillance. Complicated Type B (malperfusion, expansion, refractory pain): TEVAR — a covered stent to seal the entry tear.",
    whenUrgent: "Sudden tearing chest or interscapular pain — this is an emergency.",
    faqs: [
      { q: "Can dissection be repaired without open surgery?", a: "Type B dissections that need intervention are usually treated endovascularly with TEVAR. Type A dissections require open cardiac surgery." },
    ],
  },
  {
    slug: "aneurysm-coiling", name: "Aneurysm Coiling", hub: "aortic",
    oneLiner: "Endovascular embolisation of visceral, renal or splenic aneurysms.",
    whatIsIt:
      "Non-aortic aneurysms — splenic, renal, hepatic, mesenteric — are often found incidentally. Selected ones are treated by coiling or covered stents to prevent rupture, preserving the parent organ where possible.",
    symptoms: ["Usually silent", "Occasional flank or abdominal pain"],
    diagnosis: "CT angiography, MRA, or catheter angiography.",
    treatment:
      "Micro-catheter coiling, embolic plugs, or covered stents are chosen based on aneurysm neck, location, and parent-vessel importance.",
    faqs: [
      { q: "Do all visceral aneurysms need treatment?", a: "No — treatment thresholds depend on size, location, growth, symptoms, and specifically pregnancy planning for women." },
    ],
  },
  {
    slug: "vascular-malformations", name: "Vascular Malformations", hub: "aortic",
    oneLiner: "Congenital arterial, venous, or mixed anomalies — often lifelong, often treatable.",
    whatIsIt:
      "Vascular malformations are present from birth but may declare themselves later. They can be low-flow (venous, lymphatic) or high-flow (AVM), and behave very differently.",
    symptoms: ["Visible swelling or discolouration", "Pain, warmth, or heaviness", "Bleeding (mucosal or skin lesions)"],
    diagnosis: "MRI is the workhorse; angiography for high-flow lesions.",
    treatment:
      "Sclerotherapy for low-flow lesions; catheter embolisation for high-flow AVMs. Treatment is often staged over multiple sessions and coordinated with plastic or ENT surgery where needed.",
    faqs: [
      { q: "Will a malformation come back?", a: "Some do — this is why we plan for a series rather than a one-off, and follow long-term." },
    ],
  },
  {
    slug: "lymphatic-interventions", name: "Lymphatic Interventions", hub: "aortic",
    oneLiner: "Image-guided treatment of chylous leaks and lymphatic disorders.",
    whatIsIt:
      "The lymphatic system, long ignored by interventional medicine, is now accessible via micro-catheter techniques for chylothorax, chylous ascites, and plastic bronchitis.",
    symptoms: ["Persistent milky pleural or peritoneal fluid", "Post-surgical chylous leak", "Chronic swelling not responding to compression"],
    diagnosis: "Dynamic contrast MR lymphangiography.",
    treatment:
      "Thoracic duct embolisation or lymphangiography-guided glue closure, typically as a day-case.",
    faqs: [
      { q: "Is this widely available?", a: "No — this is one of the newer areas of IR. Referrals come from surgical teams facing persistent post-op chylous leaks." },
    ],
  },
  // — PAD & Limb Salvage —
  {
    slug: "peripheral-artery-disease", name: "Peripheral Artery Disease", hub: "pad",
    oneLiner: "Narrowed leg arteries — the vascular equivalent of coronary disease.",
    whatIsIt:
      "PAD is atherosclerosis of the leg arteries. It ranges from cramping on walking (claudication) to rest pain and ulceration (critical limb ischaemia).",
    symptoms: ["Calf or thigh cramp on walking, relieved by rest", "Cold, pale, or discoloured foot", "Non-healing foot wound", "Rest pain, especially at night"],
    diagnosis: "Ankle-brachial index (ABI), Doppler ultrasound, CT or MR angiography.",
    treatment:
      "Risk-factor control and supervised exercise for claudication. For lifestyle-limiting or limb-threatening disease: angioplasty ± stenting, atherectomy, or bypass surgery when endovascular is not appropriate.",
    whenUrgent: "A cold, pale, painful leg — this is acute ischaemia. Emergency evaluation immediately.",
    faqs: [
      { q: "Is angioplasty a permanent fix?", a: "It restores flow but not the underlying disease. Ongoing medical therapy and surveillance are essential." },
    ],
  },
  {
    slug: "foot-salvage-cli", name: "Foot Salvage / Critical Limb Ischaemia", hub: "pad",
    oneLiner: "Rest pain, ulceration, gangrene — the limb is at stake.",
    whatIsIt:
      "CLI represents end-stage PAD. Without restored flow, amputation risk within a year is high. Timely revascularisation is the intervention that most changes outcomes.",
    symptoms: ["Constant foot or toe pain, worse at night", "Non-healing ulcer or gangrenous toe", "Foot pale on elevation, purple when hanging down"],
    diagnosis: "Urgent Doppler, angiography, and toe pressures.",
    treatment:
      "Angioplasty and, where needed, stenting of tibial vessels below the knee. Coordinated with the wound-care team and, in some cases, plastic surgery for coverage.",
    whenUrgent: "Cold, dusky, painful foot needs emergency evaluation.",
    faqs: [
      { q: "Can the foot always be saved?", a: "Often, but not always — outcome depends on how long the flow has been compromised, diabetes control, and wound stage." },
    ],
  },
  {
    slug: "diabetic-foot-angioplasty", name: "Diabetic Foot Angioplasty", hub: "pad",
    oneLiner: "Below-the-knee angioplasty for the diabetic limb.",
    whatIsIt:
      "Diabetic vascular disease typically affects the small arteries below the knee — a technically demanding zone that responds well to modern low-profile balloon and drug-coated techniques.",
    symptoms: ["Non-healing foot ulcer", "Gangrene of one or more toes", "Rest pain at night"],
    diagnosis: "Doppler, angiography — angiosome-targeted planning.",
    treatment:
      "Angiosome-directed balloon angioplasty of tibial and pedal arteries, coordinated with diabetology and podiatry.",
    faqs: [
      { q: "Is angioplasty right for every diabetic ulcer?", a: "No — some ulcers are neuropathic without ischaemia. Doppler and pressure studies distinguish the two." },
    ],
  },
  {
    slug: "carotid-stenting", name: "Carotid Stenting", hub: "pad",
    oneLiner: "Endovascular treatment of narrowed carotid arteries in selected patients.",
    whatIsIt:
      "Significant carotid disease raises stroke risk. Carotid endarterectomy (open surgery) remains a mainstay; carotid stenting is chosen for specific anatomies and comorbidities.",
    symptoms: ["Prior TIA (mini-stroke) or stroke", "Amaurosis fugax (transient monocular vision loss)"],
    diagnosis: "Duplex ultrasound, CTA or MRA.",
    treatment:
      "Stenting with an embolic protection device, or referral to vascular surgery for endarterectomy — decision is multi-disciplinary.",
    faqs: [
      { q: "Stent or surgery — which is better?", a: "It depends on age, anatomy, comorbidities, and surgeon/operator experience. We recommend based on evidence, not preference." },
    ],
  },
  {
    slug: "renal-artery-stenosis", name: "Renal Artery Stenosis", hub: "pad",
    oneLiner: "Narrowed renal arteries causing resistant hypertension or kidney injury.",
    whatIsIt:
      "Renal artery narrowing can cause treatment-resistant hypertension or worsening kidney function. Only a subset benefits from intervention — case selection matters.",
    symptoms: ["Blood pressure uncontrolled despite three drugs", "Rising creatinine after ACE inhibitor start"],
    diagnosis: "Duplex ultrasound, CT/MR angiography, occasionally captopril renogram.",
    treatment:
      "Balloon angioplasty ± stenting for anatomically and clinically appropriate cases (fibromuscular dysplasia, selected atherosclerotic lesions).",
    faqs: [
      { q: "Does stenting always improve blood pressure?", a: "No — this is why we screen carefully. Many patients do better on medication alone." },
    ],
  },
  // — Venous —
  {
    slug: "deep-vein-thrombosis", name: "Deep Vein Thrombosis", hub: "venous",
    oneLiner: "A clot in a deep leg vein — usually medical, occasionally interventional.",
    whatIsIt:
      "Most DVTs are treated with anticoagulation alone. A minority — iliofemoral involvement, phlegmasia, or young active patients — benefit from catheter-directed thrombolysis or mechanical thrombectomy.",
    symptoms: ["Unilateral leg swelling", "Calf or thigh ache, warmth", "Discolouration"],
    diagnosis: "Compression ultrasound of the leg veins.",
    treatment:
      "Anticoagulation for most. For extensive proximal clot: catheter-directed thrombolysis, mechanical thrombectomy, and — when a compressive lesion is found — stenting of an underlying May-Thurner narrowing.",
    whenUrgent: "Sudden severe leg swelling and cyanosis — phlegmasia — go to emergency.",
    faqs: [
      { q: "Do I need my clot removed?", a: "Most don't. Iliofemoral DVT in an otherwise well younger patient is the strongest case." },
    ],
  },
  {
    slug: "varicose-veins", name: "Varicose Veins", hub: "venous",
    oneLiner: "Endovenous laser or radiofrequency ablation — no open stripping.",
    whatIsIt:
      "Varicose veins arise from incompetent valves in the great or small saphenous veins. Modern treatment is endovenous — a catheter and heat, day-case, walk-in walk-out.",
    symptoms: ["Aching, heaviness, night cramps", "Skin discolouration around ankles", "Bleeding varicosity, ulceration in severe cases"],
    diagnosis: "Venous duplex ultrasound.",
    treatment:
      "Endovenous laser or radiofrequency ablation of the incompetent trunk; ultrasound-guided foam sclerotherapy for tributaries; phlebectomy for cosmetic clean-up.",
    faqs: [
      { q: "Is this covered by insurance?", a: "Usually yes when there are symptoms or skin changes; cosmetic-only cases vary by policy." },
    ],
  },
  {
    slug: "ivc-filter", name: "IVC Filter", hub: "venous",
    oneLiner: "A short-term mechanical safeguard against pulmonary embolism.",
    whatIsIt:
      "An inferior vena cava filter catches large clots travelling from the legs before they reach the lungs. Modern practice: retrievable filters, placed for a defined indication and removed on schedule.",
    symptoms: ["None from the filter itself"],
    diagnosis: "Indication-driven — recent DVT with contraindication to anticoagulation, for example.",
    treatment:
      "Percutaneous jugular placement and, importantly, timely retrieval when the indication resolves.",
    faqs: [
      { q: "Do all filters have to come out?", a: "Retrievable filters should come out when no longer needed — usually within weeks to a few months." },
    ],
  },
  {
    slug: "may-thurner-syndrome", name: "May-Thurner Syndrome", hub: "venous",
    oneLiner: "A compressed left iliac vein — often the hidden cause of a young woman's DVT.",
    whatIsIt:
      "The left iliac vein is pinched by the crossing right iliac artery in a subset of people. Chronic compression can cause swelling and predispose to left-leg DVT.",
    symptoms: ["Chronic left-leg swelling, worse late in the day", "Recurrent left iliofemoral DVT"],
    diagnosis: "CT/MR venography or intravascular ultrasound during venography.",
    treatment:
      "Venous stenting of the compressed segment, with thrombolysis or thrombectomy first if there is acute clot.",
    faqs: [
      { q: "Is a venous stent permanent?", a: "Yes — dedicated venous stents are designed for lifelong placement, with anticoagulation for a defined initial period." },
    ],
  },
  {
    slug: "pulmonary-embolism", name: "Pulmonary Embolism", hub: "venous",
    oneLiner: "Catheter-directed treatment for intermediate-to-high-risk PE.",
    whatIsIt:
      "Most PEs are managed with anticoagulation alone. Intermediate-high-risk or high-risk PE — with right-heart strain, shock, or deterioration — benefits from catheter-directed thrombolysis or mechanical thrombectomy.",
    symptoms: ["Sudden shortness of breath", "Chest pain, especially with breathing", "Fainting, low blood pressure (severe cases)"],
    diagnosis: "CT pulmonary angiography, echocardiography.",
    treatment:
      "Catheter-directed thrombolysis or mechanical thrombectomy for selected intermediate-high or high-risk PE, in coordination with cardiology and ICU.",
    whenUrgent: "Sudden severe shortness of breath — go to emergency immediately.",
    faqs: [
      { q: "When is catheter treatment better than anticoagulation alone?", a: "When there is right-ventricular strain and either haemodynamic deterioration or high bleeding risk with systemic lytics." },
    ],
  },
  {
    slug: "thrombolysis", name: "Thrombolysis (Catheter-Directed)", hub: "venous",
    oneLiner: "Delivering clot-dissolving medicine directly into the clot.",
    whatIsIt:
      "Rather than systemic lysis, a micro-catheter is placed inside the clot and low-dose thrombolytic infused over hours — higher local dose, lower bleeding risk, often combined with mechanical clot removal.",
    symptoms: ["Depends on affected territory"],
    diagnosis: "Confirmed on imaging (Doppler, CT, angiography).",
    treatment:
      "Angiographic access, catheter placement into clot, infusion overnight in a monitored bed, interval imaging, and completion angiography.",
    faqs: [
      { q: "Is it risky?", a: "Bleeding is the main risk; local dose lowers systemic exposure. We screen carefully and monitor closely." },
    ],
  },
  // — Oncology —
  {
    slug: "liver-cancer-tace", name: "Liver Cancer TACE", hub: "oncology",
    oneLiner: "Trans-arterial chemoembolisation — delivering chemotherapy directly to the tumour.",
    whatIsIt:
      "TACE catheterises the hepatic artery branches feeding a liver tumour, delivers concentrated chemotherapy locally, then embolises to trap it in place. Used for hepatocellular carcinoma and selected liver metastases.",
    symptoms: ["Often none until advanced"],
    diagnosis: "Multi-phase CT/MRI, tumour markers, biopsy where needed.",
    treatment:
      "TACE is one of several tools — resection, ablation, Y-90 radioembolisation, transplant — chosen by a multidisciplinary tumour board.",
    faqs: [
      { q: "How many TACE sessions are usual?", a: "Two to four typically, spaced weeks apart, then re-imaging to reassess." },
    ],
  },
  {
    slug: "y-90-radioembolisation", name: "Y-90 Radioembolisation", hub: "oncology",
    oneLiner: "Selective internal radiation therapy for liver tumours.",
    whatIsIt:
      "Yttrium-90 microspheres are delivered via the hepatic artery. Because tumours draw their blood supply from the artery (not the portal vein), the beads lodge in the tumour bed and irradiate over days.",
    symptoms: ["Depend on underlying disease"],
    diagnosis: "Standard oncology workup plus a mapping angiogram to plan the shunt.",
    treatment:
      "Mapping angiogram, dose planning, then treatment as a day-case in most patients.",
    faqs: [
      { q: "Is Y-90 chemotherapy?", a: "No — it delivers radiation locally rather than chemotherapy. Some patients receive both, sequentially." },
    ],
  },
  {
    slug: "kidney-cancer-ablation", name: "Kidney Cancer Ablation", hub: "oncology",
    oneLiner: "Percutaneous freezing or heating of small renal tumours.",
    whatIsIt:
      "For small (<4 cm) renal masses, especially in patients unsuited to partial nephrectomy, image-guided cryoablation or microwave ablation offers a kidney-sparing curative option.",
    symptoms: ["Usually asymptomatic — found incidentally"],
    diagnosis: "CT/MRI ± biopsy.",
    treatment:
      "Day-case percutaneous ablation under CT guidance, often with an overnight observation.",
    faqs: [
      { q: "Is this as good as surgery?", a: "For small, well-selected tumours, oncological outcomes are comparable with less kidney function loss." },
    ],
  },
  {
    slug: "portal-hypertension", name: "Portal Hypertension (TIPS)", hub: "oncology",
    oneLiner: "TIPS — a bypass to relieve variceal bleeding and refractory ascites.",
    whatIsIt:
      "Trans-jugular intrahepatic portosystemic shunt connects the portal and hepatic venous systems inside the liver, dropping portal pressure. Used for recurrent variceal bleeding or refractory ascites.",
    symptoms: ["Variceal bleeding (blood in vomit or stool)", "Uncontrolled ascites"],
    diagnosis: "Endoscopy, cross-sectional imaging, hepatology assessment.",
    treatment:
      "TIPS placement — a covered stent inside the liver, connecting portal to hepatic vein. Coordinated closely with hepatology.",
    whenUrgent: "Active variceal bleeding is a medical emergency.",
    faqs: [
      { q: "Any downside to TIPS?", a: "It can precipitate encephalopathy in some patients. Case selection and dose (stent diameter) matter." },
    ],
  },
  {
    slug: "biliary-drainage", name: "Biliary Drainage", hub: "oncology",
    oneLiner: "Percutaneous drainage or stenting for obstructed bile ducts.",
    whatIsIt:
      "When ERCP fails or isn't feasible, percutaneous biliary drainage relieves jaundice and prevents cholangitis. Can be a bridge to surgery or a definitive palliative measure.",
    symptoms: ["Yellow eyes and skin", "Pale stools, dark urine", "Fever with rigors (cholangitis — emergency)"],
    diagnosis: "USG, MRCP, CT.",
    treatment:
      "Percutaneous transhepatic biliary drainage under image guidance; internalisation and metallic stenting where appropriate.",
    whenUrgent: "Fever + jaundice + right-upper-quadrant pain = cholangitis. Emergency.",
    faqs: [
      { q: "Can the drain be internalised?", a: "Usually yes — an internal-external catheter first, then internal drainage or a stent." },
    ],
  },
  {
    slug: "chemo-port-placement", name: "Chemo Port Placement", hub: "oncology",
    oneLiner: "A subcutaneous port for repeated chemotherapy access.",
    whatIsIt:
      "A chemo port is a small reservoir placed under the skin, connected to a central vein. It preserves peripheral veins during long chemotherapy regimens.",
    symptoms: ["N/A"],
    diagnosis: "Standard oncology workup.",
    treatment:
      "Day-case placement under local anaesthetic + sedation, with imaging guidance. Same-day discharge.",
    faqs: [
      { q: "Is it uncomfortable?", a: "A small bump under the skin, but most patients forget it's there once healed." },
    ],
  },
  {
    slug: "picc-line", name: "PICC Line", hub: "oncology",
    oneLiner: "Peripherally inserted central catheter for medium-term IV access.",
    whatIsIt:
      "A PICC is inserted via an arm vein and its tip sits centrally. Ideal for weeks-to-months of IV antibiotics, chemotherapy, or nutrition.",
    symptoms: ["N/A"],
    diagnosis: "Indication-driven.",
    treatment:
      "Ultrasound-guided placement under local anaesthetic, verified with X-ray or ECG guidance.",
    faqs: [
      { q: "How long can a PICC stay?", a: "Weeks to a few months, depending on care and indication." },
    ],
  },
  {
    slug: "tunnelled-catheters", name: "Tunnelled Catheters", hub: "oncology",
    oneLiner: "For long-term dialysis or chemotherapy access.",
    whatIsIt:
      "A tunnelled central venous catheter is placed with a subcutaneous cuff that anchors it and reduces infection risk — used when a port isn't suitable or a permanent line is needed.",
    symptoms: ["N/A"],
    diagnosis: "Indication-driven.",
    treatment:
      "Day-case placement under image guidance and local anaesthetic.",
    faqs: [
      { q: "Difference vs. a chemo port?", a: "A port is fully implanted; a tunnelled line exits the skin. Both have their place." },
    ],
  },
  {
    slug: "nephrostomy", name: "Nephrostomy", hub: "oncology",
    oneLiner: "Percutaneous kidney drainage for obstructed urinary systems.",
    whatIsIt:
      "When the ureter is blocked — by stone, tumour, or scar — percutaneous nephrostomy drains urine directly from the kidney, protecting kidney function and treating infection.",
    symptoms: ["Loin pain", "Fever with rigors (obstructed infection — emergency)", "Reduced urine output"],
    diagnosis: "USG, CT, blood tests.",
    treatment:
      "Ultrasound- and fluoroscopy-guided placement of a nephrostomy tube — often as a same-day procedure.",
    whenUrgent: "Fever + loin pain + obstruction — emergency.",
    faqs: [
      { q: "Can it be internalised later?", a: "Often — nephrostomy is frequently converted to an internal double-J stent by urology once the acute issue is controlled." },
    ],
  },
  // — Women's Health —
  {
    slug: "uterine-fibroids", name: "Uterine Fibroids", hub: "womens-health",
    oneLiner: "Uterine artery embolisation — day-case, uterus-sparing, evidence-based.",
    whatIsIt:
      "Fibroids are benign muscular tumours of the uterus. When symptomatic, treatment options include medication, surgery (myomectomy, hysterectomy), and uterine artery embolisation — a keyhole IR procedure that shrinks fibroids by blocking their blood supply.",
    symptoms: ["Heavy or prolonged periods", "Pelvic pressure or bulk", "Urinary frequency", "Painful periods"],
    diagnosis: "Pelvic USG and MRI.",
    treatment:
      "Uterine artery embolisation via a wrist or groin puncture, typically 24-hour admission. We coordinate with your gynaecologist and are honest about who is or isn't a good candidate (fertility plans, submucosal fibroids, etc.).",
    faqs: [
      { q: "Can I still have children after UAE?", a: "Pregnancy is possible but not guaranteed. If active fertility is a priority, myomectomy is usually preferred first." },
      { q: "How long is recovery?", a: "Most women return to normal activity in one to two weeks." },
    ],
  },
  {
    slug: "pelvic-congestion", name: "Pelvic Congestion Syndrome", hub: "womens-health",
    oneLiner: "Chronic pelvic pain from dilated pelvic veins — often missed for years.",
    whatIsIt:
      "Reflux and dilation in the ovarian and pelvic veins can cause chronic pelvic pain, especially standing pain, post-coital pain, and pain worse with the menstrual cycle.",
    symptoms: ["Dull chronic pelvic pain, worse with standing", "Pain after intercourse", "Visible perineal or vulvar varicosities"],
    diagnosis: "Doppler USG, MR venography, catheter venography.",
    treatment:
      "Ovarian and internal iliac vein embolisation — day-case, coils/glue/foam, targeting the refluxing veins.",
    faqs: [
      { q: "How soon do symptoms improve?", a: "Typically over several weeks; some patients report improvement within days." },
    ],
  },
  // — Men's Health —
  {
    slug: "prostate-artery-embolisation", name: "Prostate Artery Embolisation (PAE)", hub: "mens-health",
    oneLiner: "A keyhole alternative to surgery for benign prostate enlargement.",
    whatIsIt:
      "For men with BPH-related urinary symptoms who prefer to avoid or aren't candidates for TURP or other surgery, PAE reduces prostate volume by blocking the small arteries feeding it — day-case, preserving sexual function.",
    symptoms: ["Weak urinary stream", "Nocturia (frequent night waking to urinate)", "Incomplete emptying", "Urgency"],
    diagnosis: "Urology assessment, uroflowmetry, prostate USG or MRI.",
    treatment:
      "Femoral or wrist puncture, super-selective catheterisation of prostatic arteries, embolisation with particles. Day-case with a few hours' recovery.",
    faqs: [
      { q: "Does PAE preserve sexual function?", a: "Yes — retrograde ejaculation and erectile dysfunction rates are lower than with TURP." },
      { q: "When does the improvement start?", a: "Symptoms improve over weeks; peak effect by three to six months." },
    ],
  },
  {
    slug: "varicocele", name: "Varicocele Embolisation", hub: "mens-health",
    oneLiner: "Non-surgical treatment for scrotal varicocele — pain and fertility indications.",
    whatIsIt:
      "A varicocele is a dilated pampiniform venous plexus around the testicle. Embolisation of the refluxing internal spermatic vein is done from a small neck puncture — no scrotal surgery.",
    symptoms: ["Aching scrotal heaviness worse at day's end", "Palpable 'bag of worms' scrotal veins", "Sub-fertility with abnormal semen parameters"],
    diagnosis: "Clinical exam and scrotal Doppler.",
    treatment:
      "Catheter embolisation via right jugular puncture, coils or glue, day-case.",
    faqs: [
      { q: "Does embolisation improve fertility?", a: "Semen parameters improve in a meaningful proportion of men — outcomes depend on baseline factors." },
    ],
  },
  // — Dialysis & Emergency —
  {
    slug: "dialysis-access", name: "Dialysis Access Creation & Maintenance", hub: "dialysis-emergency",
    oneLiner: "Creating, salvaging, and protecting the vascular lifeline of a dialysis patient.",
    whatIsIt:
      "For patients on haemodialysis, the fistula or graft is life. Endovascular fistuloplasty, thrombectomy, and stenting keep access working and delay the need for new creation.",
    symptoms: ["Difficult cannulation", "Prolonged bleeding after dialysis", "Loss of thrill or bruit", "Arm swelling"],
    diagnosis: "Access ultrasound, fistulography.",
    treatment:
      "Angioplasty of stenoses, declotting of thrombosed fistulas, stent placement where recoil is severe, and creation planning with vascular surgery.",
    faqs: [
      { q: "How often do fistulas need attention?", a: "Regular surveillance and early intervention significantly extend fistula life." },
    ],
  },
  {
    slug: "av-fistula-salvage", name: "AV Fistula Salvage", hub: "dialysis-emergency",
    oneLiner: "Declotting and angioplasty for a failing or thrombosed fistula.",
    whatIsIt:
      "A thrombosed fistula is a medical urgency — the longer the delay, the harder the salvage. Mechanical thrombectomy plus underlying stenosis treatment restores access.",
    symptoms: ["No thrill or bruit", "Missed dialysis session"],
    diagnosis: "Ultrasound and immediate fistulography.",
    treatment:
      "Same-day thrombectomy and angioplasty; stent-graft where a segment is degraded.",
    whenUrgent: "A dialysis fistula with no thrill or bruit — call the practice the same day.",
    faqs: [
      { q: "How quickly should I be seen?", a: "Within 24-48 hours ideally — the sooner, the higher the salvage rate." },
    ],
  },
  {
    slug: "gi-bleed-embolisation", name: "GI Bleed Embolisation", hub: "dialysis-emergency",
    oneLiner: "Angiographic control of ongoing gastrointestinal bleeding.",
    whatIsIt:
      "When endoscopy can't localise or control a GI bleed, angiography can — visualising active extravasation and embolising the culprit vessel with coils, glue, or particles.",
    symptoms: ["Vomiting blood or coffee-ground material", "Passing black or fresh blood per rectum", "Haemodynamic instability"],
    diagnosis: "CT angiography and catheter angiography.",
    treatment:
      "Super-selective embolisation of the bleeding vessel; done in coordination with gastroenterology and the medical team.",
    whenUrgent: "Any significant GI bleed is an emergency.",
    faqs: [
      { q: "Is there a risk of bowel ischaemia?", a: "Super-selective techniques minimise this — but the risk isn't zero. Case selection matters." },
    ],
  },
  {
    slug: "haemoptysis-embolisation", name: "Haemoptysis Embolisation", hub: "dialysis-emergency",
    oneLiner: "Bronchial artery embolisation for massive or recurrent haemoptysis.",
    whatIsIt:
      "Life-threatening or recurrent haemoptysis often arises from the bronchial arteries. Embolisation is the first-line intervention and can be lifesaving.",
    symptoms: ["Coughing blood, especially in volume", "Recurrent smaller episodes"],
    diagnosis: "CT thorax with contrast, bronchoscopy.",
    treatment:
      "Selective bronchial artery embolisation with particles. Recurrence is possible; repeat embolisation is straightforward.",
    whenUrgent: "Massive haemoptysis is an emergency — go to the nearest ER.",
    faqs: [
      { q: "Does one embolisation cure the cause?", a: "It controls bleeding — the underlying disease (TB, bronchiectasis, malignancy) still needs treatment." },
    ],
  },
  {
    slug: "trauma-embolisation", name: "Trauma Embolisation", hub: "dialysis-emergency",
    oneLiner: "Emergency embolisation for solid-organ or pelvic bleeding.",
    whatIsIt:
      "Splenic, hepatic, renal, and pelvic bleeding in trauma can often be controlled by embolisation — sparing surgery and preserving organs.",
    symptoms: ["Post-trauma haemodynamic instability", "Active bleeding on trauma CT"],
    diagnosis: "Trauma-protocol CT.",
    treatment:
      "Selective or non-selective embolisation depending on the organ, injury grade, and patient stability, in coordination with trauma surgery.",
    whenUrgent: "Trauma is a hospital-emergency problem; this describes what may be done once you're there.",
    faqs: [
      { q: "Can the spleen be saved?", a: "Often — angioembolisation salvage rates for high-grade splenic injuries are very good in experienced hands." },
    ],
  },
];

export const getHub = (slug) => HUBS.find((h) => h.slug === slug);
export const getCondition = (slug) => CONDITIONS.find((c) => c.slug === slug);
export const conditionsForHub = (hubSlug) => CONDITIONS.filter((c) => c.hub === hubSlug);
